import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, Package, School as SchoolIcon, Store, ShoppingBag, Wallet, Plus, Search,
  Edit3, ToggleLeft, ToggleRight, RefreshCw, X, UserRoundCog, KeyRound,
  Users, CheckCircle2, Copy, ChevronDown, ChevronRight,
  BarChart3, MapPin, Phone, Mail, Building2, AlertTriangle,
  Settings as SettingsIcon,
} from "lucide-react";

const NAV = [
  { to: "/super/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/super/schools", label: "Schools", icon: SchoolIcon },
  { to: "/super/products", label: "Products", icon: Package },
  { to: "/super/vendors", label: "Vendors", icon: Store },
  { to: "/super/orders", label: "Orders & Ops", icon: ShoppingBag },
  { to: "/super/finance", label: "Finance", icon: Wallet },
  { to: "/super/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/super/settings", label: "Settings", icon: SettingsIcon },
];

const EMPTY_SCHOOL = {
  name: "", city: "", state: "", board: "CBSE", tenant_type: "pilot",
  annual_budget: "", student_strength: "",
  address_line1: "", address_line2: "", pincode: "",
  gstin: "", website: "",
  contact_persons: [], relationship_manager_id: "",
  active: true, notes: "",
};

const BOARDS = ["CBSE", "ICSE", "IB", "State Board", "Cambridge", "Other"];
const TENANT_TYPES = [
  { v: "pilot", l: "Pilot" },
  { v: "paying", l: "Paying" },
  { v: "demo", l: "Demo" },
];

function fmtINR(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

export default function Schools() {
  const [rows, setRows] = useState([]);
  const [rms, setRms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (tenantFilter) params.tenant_type = tenantFilter;
      if (activeFilter) params.active = activeFilter;
      const [{ data }, rmRes] = await Promise.all([
        api.get("/admin/schools", { params }),
        rms.length ? Promise.resolve({ data: rms }) : api.get("/admin/schools/relationship-managers"),
      ]);
      setRows(data);
      if (!rms.length) setRms(rmRes.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.active !== false).length;
    const paying = rows.filter((r) => r.tenant_type === "paying").length;
    const totalStudents = rows.reduce((s, r) => s + (r.student_strength || 0), 0);
    return { total, active, inactive: total - active, paying, totalStudents };
  }, [rows]);

  const toggleActive = async (r) => {
    try {
      if (r.active !== false) {
        await api.delete(`/admin/schools/${r.id}`);
        setRows((s) => s.map((x) => (x.id === r.id ? { ...x, active: false } : x)));
        toast.success(`${r.name} deactivated`);
      } else {
        const { data } = await api.patch(`/admin/schools/${r.id}`, { active: true });
        setRows((s) => s.map((x) => (x.id === r.id ? { ...x, ...data } : x)));
        toast.success(`${r.name} reactivated`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async (form) => {
    try {
      const payload = {
        name: form.name.trim(),
        city: form.city || "",
        state: form.state || "",
        board: form.board || "",
        tenant_type: form.tenant_type || "pilot",
        annual_budget: Number(form.annual_budget || 0),
        student_strength: parseInt(form.student_strength || 0, 10),
        address_line1: form.address_line1 || "",
        address_line2: form.address_line2 || "",
        pincode: form.pincode || "",
        gstin: form.gstin || "",
        website: form.website || "",
        contact_persons: (form.contact_persons || []).filter((c) => c.name?.trim()),
        relationship_manager_id: form.relationship_manager_id || null,
        active: !!form.active,
        notes: form.notes || "",
      };
      if (form.id) {
        const { data } = await api.patch(`/admin/schools/${form.id}`, payload);
        setRows((s) => s.map((x) => (x.id === form.id ? { ...x, ...data,
          users_count: x.users_count, orders_count: x.orders_count } : x)));
        toast.success("School updated");
      } else {
        const { data } = await api.post("/admin/schools", payload);
        setRows((s) => [{ ...data, users_count: 0, orders_count: 0 }, ...s]);
        toast.success("School added");
      }
      setEditing(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout nav={NAV} title="School Management">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="text-2xl font-heading font-semibold text-brand-navy" data-testid="schools-title">
            Schools
          </div>
          <div className="text-sm text-slate-500">
            {stats.total} total · {stats.active} active · {stats.paying} paying · {stats.totalStudents.toLocaleString("en-IN")} students
          </div>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY_SCHOOL })}
          data-testid="schools-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark"
        >
          <Plus size={14} /> Add School
        </button>
      </div>

      <div className="sl-card p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="schools-search"
            placeholder="Search name, city, state, GSTIN…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 pr-4 py-2 w-full rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue"
          />
        </div>
        <select
          data-testid="schools-tenant-filter"
          value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="">All tenants</option>
          {TENANT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
        <select
          data-testid="schools-active-filter"
          value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="">Any status</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
        <button
          onClick={load}
          data-testid="schools-apply-filters"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-700"
        >
          Apply
        </button>
        <button
          onClick={() => { setQ(""); setTenantFilter(""); setActiveFilter(""); setTimeout(load, 0); }}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 text-sm hover:border-slate-400"
        >
          <RefreshCw size={13} /> Clear
        </button>
      </div>

      <div className="sl-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">School</th>
              <th className="text-left px-4 py-3">Location</th>
              <th className="text-left px-4 py-3">Board</th>
              <th className="text-left px-4 py-3">Tenant</th>
              <th className="text-right px-4 py-3">Users</th>
              <th className="text-right px-4 py-3">Orders</th>
              <th className="text-left px-4 py-3">RM</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center py-10 text-slate-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-slate-500" data-testid="schools-empty">
                No schools match your filters.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer"
                  data-testid={`school-row-${r.id}`}
                  onClick={() => setDetailId(r.id)}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-800">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.masked_ref} · {(r.student_strength || 0).toLocaleString("en-IN")} students</div>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {r.city ? `${r.city}, ${r.state || "—"}` : "—"}
                </td>
                <td className="px-4 py-3 text-slate-600">{r.board || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    r.tenant_type === "paying" ? "bg-brand-greenSoft text-brand-greenDark"
                    : r.tenant_type === "pilot" ? "bg-brand-blueSoft text-brand-blueDark"
                    : "bg-slate-100 text-slate-600"
                  }`}>{r.tenant_type || "—"}</span>
                </td>
                <td className="px-4 py-3 text-right text-slate-700">{r.users_count}</td>
                <td className="px-4 py-3 text-right text-slate-700">{r.orders_count}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{r.relationship_manager_name || <span className="text-slate-400">Unassigned</span>}</td>
                <td className="px-4 py-3 text-center">
                  {r.active !== false ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-greenSoft text-brand-greenDark text-xs font-semibold">
                      <CheckCircle2 size={12} /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">Inactive</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => setEditing({
                        ...r,
                        annual_budget: String(r.annual_budget || ""),
                        student_strength: String(r.student_strength || ""),
                      })}
                      data-testid={`school-edit-${r.id}`}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Edit"
                    >
                      <Edit3 size={15} />
                    </button>
                    <button
                      onClick={() => toggleActive(r)}
                      data-testid={`school-toggle-${r.id}`}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                      title={r.active !== false ? "Deactivate" : "Reactivate"}
                    >
                      {r.active !== false ? <ToggleRight size={17} className="text-brand-green" /> : <ToggleLeft size={17} className="text-slate-400" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <SchoolEditor
          initial={editing}
          rms={rms}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
      {detailId && (
        <SchoolDetail
          id={detailId}
          rms={rms}
          onClose={() => setDetailId(null)}
          onEdit={(s) => { setDetailId(null); setEditing({
            ...s,
            annual_budget: String(s.annual_budget || ""),
            student_strength: String(s.student_strength || ""),
          }); }}
          onRefresh={load}
        />
      )}
    </DashboardLayout>
  );
}

/* ------------- Editor drawer ------------- */
function SchoolEditor({ initial, rms, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(f.id);
  const set = (k) => (e) => setF((s) => ({
    ...s,
    [k]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e,
  }));

  const setContact = (idx, k, val) => setF((s) => ({
    ...s,
    contact_persons: s.contact_persons.map((c, i) => (i === idx ? { ...c, [k]: val } : c)),
  }));
  const addContact = () => setF((s) => ({
    ...s,
    contact_persons: [...(s.contact_persons || []), { name: "", role: "", email: "", phone: "" }],
  }));
  const rmContact = (idx) => setF((s) => ({
    ...s,
    contact_persons: s.contact_persons.filter((_, i) => i !== idx),
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("School name is required");
    setSaving(true);
    await onSave(f);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="school-editor">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="w-full max-w-2xl bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">
              {isEdit ? "Edit school" : "Add school"}
            </div>
            <h3 className="mt-1 text-lg font-heading font-semibold text-brand-navy">
              {isEdit ? f.name : "New school onboarding"}
            </h3>
          </div>
          <button type="button" onClick={onClose} data-testid="school-editor-close"
                  className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          {/* Profile */}
          <section>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Profile</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600">School name *</label>
                <input required data-testid="school-name"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.name} onChange={set("name")} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">City</label>
                <input data-testid="school-city"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.city} onChange={set("city")} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">State</label>
                <input data-testid="school-state"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.state} onChange={set("state")} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Board</label>
                <select data-testid="school-board" value={f.board} onChange={set("board")}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 bg-white">
                  {BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Tenant type</label>
                <select data-testid="school-tenant" value={f.tenant_type} onChange={set("tenant_type")}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 bg-white">
                  {TENANT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Annual budget (₹)</label>
                <input type="number" min="0" data-testid="school-budget"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.annual_budget} onChange={set("annual_budget")} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Student strength</label>
                <input type="number" min="0" data-testid="school-strength"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.student_strength} onChange={set("student_strength")} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600">Address line 1</label>
                <input className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.address_line1} onChange={set("address_line1")} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600">Address line 2</label>
                <input className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.address_line2} onChange={set("address_line2")} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Pincode</label>
                <input className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.pincode} onChange={set("pincode")} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">GSTIN</label>
                <input className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase"
                  value={f.gstin} onChange={set("gstin")} placeholder="15 chars" maxLength={15} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600">Website</label>
                <input className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                  value={f.website} onChange={set("website")} placeholder="https://…" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600">Relationship Manager</label>
                <select data-testid="school-rm" value={f.relationship_manager_id || ""}
                  onChange={set("relationship_manager_id")}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 bg-white">
                  <option value="">Unassigned</option>
                  {rms.map((rm) => <option key={rm.id} value={rm.id}>{rm.name || rm.email}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Contacts */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Contact persons</div>
              <button type="button" onClick={addContact} data-testid="school-add-contact"
                className="text-xs font-semibold text-brand-blue hover:text-brand-blueDark inline-flex items-center gap-1">
                <Plus size={12} /> Add contact
              </button>
            </div>
            {(!f.contact_persons || f.contact_persons.length === 0) && (
              <div className="rounded border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
                No contacts yet — add principal, accountant, librarian.
              </div>
            )}
            <div className="space-y-3">
              {(f.contact_persons || []).map((c, idx) => (
                <div key={idx} className="grid sm:grid-cols-4 gap-2 rounded-lg border border-slate-200 p-3">
                  <input placeholder="Name" value={c.name || ""}
                    data-testid={`school-contact-name-${idx}`}
                    onChange={(e) => setContact(idx, "name", e.target.value)}
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm" />
                  <input placeholder="Role" value={c.role || ""}
                    onChange={(e) => setContact(idx, "role", e.target.value)}
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm" />
                  <input placeholder="Email" value={c.email || ""}
                    onChange={(e) => setContact(idx, "email", e.target.value)}
                    className="rounded border border-slate-300 px-2 py-1.5 text-sm" />
                  <div className="flex items-center gap-1">
                    <input placeholder="Phone" value={c.phone || ""}
                      onChange={(e) => setContact(idx, "phone", e.target.value)}
                      className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm" />
                    <button type="button" onClick={() => rmContact(idx)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-500"
                      title="Remove"><X size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <label className="text-xs font-semibold text-slate-600">Internal notes</label>
            <textarea rows={2} value={f.notes} onChange={set("notes")}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
          </section>
        </div>

        <div className="p-6 border-t border-slate-100 flex items-center gap-3">
          <button type="submit" disabled={saving} data-testid="school-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:opacity-60">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Add school")}
          </button>
          <button type="button" onClick={onClose}
            className="px-5 py-2.5 rounded-md border border-slate-300 hover:border-slate-400">Cancel</button>
        </div>
      </form>
    </div>
  );
}

/* ------------- Detail drawer ------------- */
function SchoolDetail({ id, rms, onClose, onEdit, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rmDraft, setRmDraft] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPw, setResetPw] = useState(null); // {email, new_password}

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/schools/${id}`);
      setData(d);
      setRmDraft(d.school.relationship_manager_id || "");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveRm = async () => {
    try {
      await api.post(`/admin/schools/${id}/assign-rm`, { relationship_manager_id: rmDraft || null });
      toast.success("Relationship Manager updated");
      onRefresh();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resetPassword = async (email, generate) => {
    try {
      const payload = { target_email: email };
      if (!generate) return;
      const { data: r } = await api.post(`/admin/schools/${id}/reset-user-password`, payload);
      setResetPw(r);
      toast.success("Temporary password generated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="school-detail">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4 sticky top-0 bg-white z-10">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">
              School profile
            </div>
            <h3 className="mt-1 text-xl font-heading font-semibold text-brand-navy truncate">
              {data?.school?.name || "Loading…"}
            </h3>
            {data?.school && (
              <div className="mt-1 text-xs text-slate-500">
                {data.school.masked_ref} · {data.school.city ? `${data.school.city}, ${data.school.state}` : "Location not set"}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {data?.school && (
              <button onClick={() => onEdit(data.school)} data-testid="school-detail-edit"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 hover:border-slate-400 text-sm">
                <Edit3 size={13} /> Edit
              </button>
            )}
            <button onClick={onClose} data-testid="school-detail-close"
              className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
          </div>
        </div>

        {loading || !data ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : (
          <div className="p-6 space-y-6 text-sm">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Users} label="Users" value={data.stats.users_total} />
              <StatCard icon={ShoppingBag} label="Orders" value={data.stats.orders_total} />
              <StatCard icon={BarChart3} label="PO Spend" value={fmtINR(data.stats.purchase_orders_spend)} />
              <StatCard icon={BarChart3} label="Order Spend" value={fmtINR(data.stats.orders_spend)} />
            </div>

            {/* Relationship Manager */}
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <UserRoundCog size={16} className="text-brand-blue" />
                <div className="font-heading font-semibold text-brand-navy">Relationship Manager</div>
              </div>
              <div className="flex items-center gap-2">
                <select value={rmDraft} onChange={(e) => setRmDraft(e.target.value)}
                  data-testid="school-detail-rm-select"
                  className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
                  <option value="">— Unassigned —</option>
                  {rms.map((rm) => <option key={rm.id} value={rm.id}>{rm.name || rm.email}</option>)}
                </select>
                <button onClick={saveRm} data-testid="school-detail-rm-save"
                  className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark">
                  Assign
                </button>
              </div>
              {data.relationship_manager && (
                <div className="mt-2 text-xs text-slate-500">
                  Currently: <span className="font-semibold text-slate-700">{data.relationship_manager.name}</span>
                  {" · "}{data.relationship_manager.email}
                </div>
              )}
            </section>

            {/* Contacts */}
            {data.school.contact_persons?.length > 0 && (
              <section>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Contact persons</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.school.contact_persons.map((c, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="font-semibold text-slate-800">{c.name}</div>
                      {c.role && <div className="text-xs text-slate-500">{c.role}</div>}
                      {c.email && <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-600"><Mail size={12} /> {c.email}</div>}
                      {c.phone && <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600"><Phone size={12} /> {c.phone}</div>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Users + password reset */}
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound size={16} className="text-brand-blue" />
                <div className="font-heading font-semibold text-brand-navy">School users</div>
                <span className="text-xs text-slate-500 ml-1">({data.users.length})</span>
              </div>
              {resetPw && (
                <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs" data-testid="school-detail-reset-result">
                  <div className="flex items-center gap-2 text-amber-800 font-semibold">
                    <AlertTriangle size={13} /> Copy this password now — it will not be shown again.
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 bg-white border border-amber-200 rounded px-3 py-2 font-mono text-sm text-slate-800">{resetPw.new_password}</code>
                    <button onClick={() => { navigator.clipboard.writeText(resetPw.new_password); toast.success("Copied"); }}
                      className="p-2 rounded hover:bg-white/60"><Copy size={13} /></button>
                    <button onClick={() => setResetPw(null)} className="text-amber-800 text-xs">Dismiss</button>
                  </div>
                  <div className="mt-1 text-amber-700">For: {resetPw.email}</div>
                </div>
              )}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 rounded border border-slate-100 hover:border-slate-200 px-3 py-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{u.name || u.email}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {u.email} · <span className="uppercase tracking-wide">{u.approval_role || u.role}</span>
                        {u.force_password_change && <span className="ml-2 text-amber-600 font-semibold">reset pending</span>}
                      </div>
                    </div>
                    <button onClick={() => resetPassword(u.email, true)}
                      data-testid={`school-user-reset-${u.id}`}
                      className="text-xs font-semibold text-brand-blue hover:text-brand-blueDark whitespace-nowrap">
                      Reset password
                    </button>
                  </div>
                ))}
                {data.users.length === 0 && (
                  <div className="text-xs text-slate-500 text-center py-4">No users yet.</div>
                )}
              </div>
            </section>

            {/* Recent activity */}
            <section>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Recent orders</div>
              {data.recent_orders.length === 0 ? (
                <div className="text-xs text-slate-500 rounded border border-dashed border-slate-200 p-4 text-center">
                  No orders yet from this school.
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {data.recent_orders.map((o) => (
                    <div key={o.id} className="flex items-center justify-between px-3 py-2 text-xs">
                      <div>
                        <div className="font-semibold text-slate-800">Order #{(o.id || "").slice(0, 8)}</div>
                        <div className="text-slate-500">{o.status || "—"} · {new Date(o.created_at).toLocaleDateString("en-IN")}</div>
                      </div>
                      <div className="font-semibold text-slate-700">{fmtINR(o.total)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide">
        <Icon size={13} /> {label}
      </div>
      <div className="mt-1.5 text-xl font-heading font-bold text-brand-navy">{value}</div>
    </div>
  );
}
